import { DialogOptions } from 'components/core/Dialog/common/Dialog';
import { useCallback, useRef } from 'react';
import { FormattedMessage } from 'intl';
import { translationKeyToString } from 'intl/types/translationKey';
import { DialogTranslationKeys } from 'intl/keys';

import { showErrorToastWithMessage } from 'services/toaster';
import { issueClient } from 'shared/domain/issue/issueClient';
import {
  IssueAccessChange,
  IssueAccessChangeKind,
  isAccessAlreadyGranted,
} from 'shared/domain/issue/issueAccessChange';
import { logError } from 'setup/logger/logError';
import { debugLog } from 'shared/logger/debugLog';
import { findMentionedUsersWithoutEditPermissionAndWithProjectAccess } from 'shared/domain/issue/access/findMentionedUsersWithoutEditPermissionAndWithProjectAccess';
import { AccessLevelModel } from 'shared/domain/issue/mapping/accessLevel/model/types';
import { UserAccess } from 'shared/domain/issue/mapping/accessLevel/toOnView/types';
import { LabelledUserWithSitesAndProcesses } from 'shared/domain/user/mapping/toView';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { deduplicateMentionedUsers } from 'views/issue/events/EventDialog/helpers';
import { AccessManagement } from './management';
import { MicroManagement } from './microManagementDialog';
import { CommonTranslationKeys } from 'intl/keys';
import { useIssueStore } from 'store/issue';
import {
  AccessChange,
  AccessModifierFunction,
  ManagePermissionsProps,
  OnAccessChange,
  UseManagement,
} from './types';

export function useManagement(): UseManagement {
  const saveUserAccessLocally = useIssueStore.use.saveUserAccessLocally();
  const accesses = useRef<HashMap<AccessChange>>({});
  const setAccesses = useCallback(
    (modifierFunction: AccessModifierFunction): void => {
      modifierFunction(accesses.current);
    },
    []
  );

  const onAccessChange: OnAccessChange = useCallback(
    (
      selectedValue: AccessLevelModel,
      currentValue: AccessLevelModel,
      userId: string,
      userLabel: string,
      userAccesses: UserAccess[]
    ): void => {
      if (selectedValue === currentValue) {
        setAccesses((prev) => {
          delete prev[userId];
        });
      } else {
        const access = userAccesses.find(
          (access) => access.accessingUser === userId
        );
        setAccesses((prev) => {
          prev[userId] = {
            value: selectedValue,
            access: access,
            userId: userId,
            userLabel: userLabel,
          };
        });
      }
    },
    [setAccesses]
  );

  function insufficientAccessDialog(
    users: LabelledUserWithSitesAndProcesses[],
    usersWithoutEdit: LabelledEntity[],
    issueUserAccesses: UserAccess[],
    issueId: string,
    siteId: string,
    processId: string
  ): DialogOptions {
    return {
      title: (
        <FormattedMessage id={DialogTranslationKeys.userAccessChangeAccessTitle} />
      ),
      description: (
        <MicroManagement
          users={users}
          usersWithoutEdit={usersWithoutEdit}
          issueUserAccesses={issueUserAccesses}
          siteId={siteId}
          processId={processId}
          onChange={onAccessChange}
        />
      ),
      customControllerLabels: [
        translationKeyToString(CommonTranslationKeys.cancel),
        translationKeyToString(CommonTranslationKeys.change),
      ],
      fullWidth: true,
      onDialogSubmit: (): void => {
        submitAccessChange(issueId);
      },
      catchOnCancel: true,
    };
  }

  function managementDialog(
    site: string,
    process: string,
    issueId: string,
    userAccesses: UserAccess[]
  ): DialogOptions {
    return {
      title: (
        <FormattedMessage id={DialogTranslationKeys.userAccessChangeAccessTitle} />
      ),
      description: (
        <AccessManagement
          site={site}
          process={process}
          userAccesses={userAccesses}
          onChange={onAccessChange}
        />
      ),
      customControllerLabels: [
        translationKeyToString(CommonTranslationKeys.cancel),
        translationKeyToString(CommonTranslationKeys.save),
      ],
      fullWidth: true,
      onDialogSubmit: (): void => {
        submitAccessChange(issueId);
      },
    };
  }

  function managePermissions({
    eventDescriptionWithMarkup,
    issueManagers,
    issue,
    createDialog,
  }: ManagePermissionsProps): Promise<void> {
    const mentionedUsersUnique = deduplicateMentionedUsers(
      eventDescriptionWithMarkup
    );

    const usersWithoutEditPermission =
      findMentionedUsersWithoutEditPermissionAndWithProjectAccess(
        issueManagers,
        mentionedUsersUnique,
        issue.userAccesses
      );

    if (usersWithoutEditPermission.length) {
      return createDialog(
        insufficientAccessDialog(
          issueManagers,
          usersWithoutEditPermission,
          issue.userAccesses,
          issue._id,
          issue.primaryData.site._id,
          issue.process._id
        )
      );
    }
    return Promise.resolve();
  }

  function submitAccessChange(issueId: string): void {
    Object.entries(accesses.current).forEach(([key, access]) => {
      return issueClient
        .changeAccess(toIssueAccessChange(access, issueId))
        .then((result) => {
          if (accesses[key] === access) {
            delete accesses[key];
          }
          // Another admin granted it meanwhile, or the list was stale: the wanted state holds.
          if (isAccessAlreadyGranted(result)) {
            debugLog(
              `Skipping duplicate access grant for user ${access.userId} on issue ${issueId} - access already exists.`
            );
            return;
          }

          saveUserAccessLocally(issueId, result.access);
        })
        .catch(function onError(e: Error): void {
          showErrorToastWithMessage(`access_change_failed`, {
              userLabel: access.userLabel,
            });
          logError(`Submit access error. ${e.message}`, e);
        });
    });
  }

  return {
    insufficientAccessDialog,
    managementDialog,
    managePermissions,
    onAccessChange,
    submitAccessChange,
  };
}

function toIssueAccessChange(
  accessChange: AccessChange,
  issueId: string
): IssueAccessChange {
  const body = {
    accessLevel: accessChange.value,
    accessingUser: accessChange.userId,
  };
  if (!accessChange.access) {
    if (accessChange.value === AccessLevelModel.NONE) {
      throw new Error(
        `Attempt to revoke access that does not exist. User: ${accessChange.userLabel} (${accessChange.userId}); Issue: ${issueId}`
      );
    }
    return { kind: IssueAccessChangeKind.grant, issueId, body };
  }

  const accessId = accessChange.access._id;
  return accessChange.value === AccessLevelModel.NONE
    ? { kind: IssueAccessChangeKind.revoke, issueId, accessId }
    : { kind: IssueAccessChangeKind.edit, issueId, accessId, body };
}
